import React, { useContext, useState } from 'react';
import { Button, ModalBody, ModalHeader } from 'reactstrap';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Dinero from 'dinero.js';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { OrderContext } from '../../../context/OrderContext';
import ArticleQuantity from '../../../components/ArticleQuantity';
import ToastSuccessMessage from '../../../components/ToastSuccessMessage';
import ImageGallery from './ImageGallery';
import IArticleModal from '../interfaces/IArticleModal';
import {
  ARTICLE_MODAL_IMAGE_DIMENSIONS,
  ARTICLE_MODAL_THUMBNAIL_DIMENSIONS,
  ARTICLE_SWIPE_MODAL_IMAGE_DIMENSIONS,
  ITEMS_LEFT_WARNING_COUNT,
} from '../constants';
import useMediaQuery from '../../../utils/hooks/mediaQuery.hook';
import ImageCarousel from './ImageCarousel';
import SwipeModal from '../../../components/SwipeModal';
import TextTruncate from '../../../components/TextTruncate';
import SelectFeatures from './SelectFeatures';

import { ReactComponent as BasketSmallIcon } from '../../../assets/icons/basket-sm.svg';
import { isValidObjectId } from '../../../utils/helpers';
import { CampaignContext } from '../../../context/CampaignWrapper';

const ArticleModal = ({
  setArticleId,
  article: {
    _id: articleId,
  },
  closeArticleModal,
  articleVariants,
} : IArticleModal) => {
  const { order: { quantities }, setArticleQuantity } = useContext(OrderContext);
  const { campaign: { interfaceConfig: { hideArticleCounter } } } = useContext(CampaignContext);
  const [quantity, setQuantity] = useState(1);
  const [translate] = useTranslation();
  const { isMobile } = useMediaQuery();
  const [variantIndex, setVariantIndex] = useState(articleVariants.findIndex((a) => a._id === articleId));

  const {
    itemsLeft, imageUrls, price, name, description,
  } = articleVariants[variantIndex] || {};

  const { groupId } = articleVariants[0] || {};

  const isOpen = !!articleId;

  const hasLowItemsLeft = !!itemsLeft && itemsLeft < ITEMS_LEFT_WARNING_COUNT;
  const isOutOfStock = itemsLeft === 0;

  const articlePrice = price && Dinero({ amount: price }).toFormat();

  const iconStyle = { verticalAlign: 'sub' };

  const buttonLabel = isOutOfStock
    ? translate('COMMON.OUT_OF_STOCK')
    : (
      <>
        {isMobile && <BasketSmallIcon style={iconStyle} />}
        {' '}
        <span>
          {translate('COMPONENTS.ARTICLE_MODAL.ADD_TO_CART')}
        </span>
      </>
    );

  const modalStyle = isMobile ? 'swipe-modal' : 'article-modal';

  const toggle = () => {
    if (isOpen) closeArticleModal();
  };

  const addArticleToCart = () => {
    const currentQuantityInCart = quantities[articleId] || 0;
    const newQuantity = currentQuantityInCart + quantity;

    setArticleQuantity(articleId, newQuantity);

    toast.success(<ToastSuccessMessage message={translate('COMMON.ITEM_ADDED')} />, {
      toastId: articleId,
    });

    return closeArticleModal();
  };

  const articleImages = isMobile
    ? <ImageCarousel imageUrls={imageUrls} alt={name} imageSize={ARTICLE_SWIPE_MODAL_IMAGE_DIMENSIONS} />
    : (
      <ImageGallery
        imageUrls={imageUrls || undefined}
        alt={name}
        imageSize={ARTICLE_MODAL_IMAGE_DIMENSIONS}
        thumbnailSize={ARTICLE_MODAL_THUMBNAIL_DIMENSIONS}
      />
    );

  return (
    <SwipeModal
      isOpen={isOpen}
      toggle={toggle}
      className={modalStyle}
      closeModal={closeArticleModal}
    >
      <ModalHeader toggle={toggle} className="article-modal-header" />
      <ModalBody>
        <div className="d-flex flex-column flex-md-row align-items-center align-items-md-start">
          <div className="p-md-3 text-center image-wrapper">
            {articleImages}
          </div>
          <div className="p-3 px-md-4 w-100">
            <div className="px-0">
              <h5 className="mb-3">{name || (!isValidObjectId(groupId) && groupId)}</h5>
              {hasLowItemsLeft && (
                <h6 className="text-warning">
                  {translate('COMPONENTS.ARTICLE_MODAL.ITEMS_AVAILABLE', {
                    count: itemsLeft,
                  })}
                </h6>
              )}
              {description && <TextTruncate text={description} />}
            </div>
            <hr />
            <SelectFeatures
              variantIndex={variantIndex}
              setVariantIndex={(i) => {
                setVariantIndex(i);
                if (articleVariants[i]) setArticleId(articleVariants[i]._id);
              }}
              variants={articleVariants}
            />
            <div className="mt-2 px-0 article-modal-price-section">
              {variantIndex >= 0 && (
              <div className="mb-3">
                {!isMobile && (
                <div className="d-flex justify-content-between">
                  <h6 className="text-start lh-lg">
                    {translate('COMPONENTS.ARTICLE_MODAL.PRICE')}
                  </h6>
                  {!hideArticleCounter && (
                  <h6 className="text-end lh-lg">
                    {translate('COMPONENTS.ARTICLE_MODAL.QUANTITY')}
                  </h6>
                  )}
                </div>
                )}
                <div className="d-flex justify-content-between align-items-center my-4 my-md-auto">
                  <h5 className="mb-0">
                    {articlePrice}
                  </h5>
                  {!isOutOfStock && variantIndex >= 0 && !hideArticleCounter && (
                  <ArticleQuantity quantity={quantity} setQuantity={setQuantity} maxQuantity={itemsLeft || undefined} />
                  )}
                </div>
              </div>
              )}
              <Button
                className="w-100 text-white"
                color="primary"
                disabled={isOutOfStock || variantIndex < 0}
                onClick={addArticleToCart}
              >
                {buttonLabel}
              </Button>
            </div>
          </div>
        </div>
      </ModalBody>
    </SwipeModal>
  );
};

export default ArticleModal;
